import _asyncToGenerator from "@babel/runtime-corejs3/helpers/esm/asyncToGenerator";
import _regeneratorRuntime from "@babel/runtime-corejs3/regenerator";
import _Promise from "@babel/runtime-corejs3/core-js-stable/promise";
/*
 * Licensed Materials - Property of IBM
 *
 * PID 5725-H26
 *
 * Copyright IBM Corporation 2020,2021. All Rights Reserved.
 *
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Authentication, SystemConfigurationActions, SSOVerifierActions, SSOVerifierSelectors, SSOAuthentication } from '@spm/core';
import AppSpinner from '../AppSpinner/AppSpinnerComponent';

/**
 * Logs the user in as the 'publiccitizen' account.
 *
 * If we are not authenticated at all we need to log in as PublicCitizen so that we can access the system configuration
 *
 * @param {function} dispatch function that dispatches values to Redux
 * @returns {Promise} Promise object represents if the login as public citizen completed
 */
function loginAsPublicCitizen(dispatch) {
  return new _Promise(function (resolve) {
    /**
     * Loads the system configuration if the previous call was successful.
     * @param {success} boolean
     */
    var callback = function callback(success) {
      if (success) {
        SystemConfigurationActions.getSystemPropertiesAction(dispatch, function () {
          resolve();
        });
      }
    };
    if (Authentication.userTypeIs([Authentication.USER_TYPES.NO_USER_TYPE])) {
      Authentication.loginAsPublicCitizen(callback);
    } else {
      SystemConfigurationActions.getSystemPropertiesAction(dispatch, function () {
        resolve();
      });
    }
  });
}

/**
 *  Performs the against the Single-Sign-On for SP-init flow.
 *
 * The precheck is responsible for retrieving the user account only if the user is authenticated with the IdP.
 * For the SP-init flow, there is additional calls between the client and the application server/IdP.
 *
 * @param {function} dispatch function that dispatches values to Redux
 * @see SSOVerifier.executeSSOPreCheck
 */
function doSPInitSSOPrecheck(_x) {
  return _doSPInitSSOPrecheck.apply(this, arguments);
}
/**
 * Performs the SSO precheck against the Single-Sign-On for IdP-init flow.
 *
 * The precheck is responsible for retrieving the user account only if the user is authenticated with the IdP.
 *
 * @param {function} dispatch function that dispatches values to Redux
 * @see SSOVerifier.executeSSOPreCheck
 */
function _doSPInitSSOPrecheck() {
  _doSPInitSSOPrecheck = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(dispatch) {
    var isPrecheckSuccess, _yield$SSOAuthenticat, response, idpSamlRequestData, idpResponse, idpSamlResponseData, isLoggedInTheSSO, acsURLResponse, getUserAccountResponse;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) switch (_context.prev = _context.next) {
        case 0:
          // flag to indicate if we need to use fallback behaviour
          isPrecheckSuccess = false; // Call a protected API resource (i.e. login-protected content) from the application server.
          // This call will be intercepted by the application server's Trust Assertion Interceptor.
          // The TAI responds a HTTP response with the SAML request message to be sent to the IdP.
          // The success status of the call to the protected resource is irrelevant as different application servers can respond with SAML messages but with different HTTP codes.
          _context.next = 3;
          return SSOAuthentication.callProtectedResource();
        case 3:
          _yield$SSOAuthenticat = _context.sent;
          response = _yield$SSOAuthenticat.response;
          // The SAML message is parsed to extract the SAMLRequestKey and optional RelayState.
          idpSamlRequestData = SSOAuthentication.parseSAMLRequestPayload(response); // If there isn't a SAML request key, then there is a general problem (like the AuthReqest not being generated).
          // If there is a SAMLRequestKey, we can proceed to contact the IdP to determine the current auth status of the user.
          if (!idpSamlRequestData.samlRequestKey) {
            _context.next = 24;
            break;
          }
          SSOAuthentication.clearCookiesAndSessionStorage();

          // Send the SAMLRequestKey (and optional SAML relay state which is required when provided) to the IdP.
          _context.next = 10;
          return SSOAuthentication.callIdpSAMLLoginWithSAMLRequest(idpSamlRequestData);
        case 10:
          idpResponse = _context.sent;
          if (!(idpResponse.success && idpResponse.response)) {
            _context.next = 24;
            break;
          }
          // The IdP will respond with either a SAML message or a login form depending on the auth status of the user.
          // If the SAML message contains a SAMLResponseKey, then the user is already authenticated with the IdP.
          idpSamlResponseData = SSOAuthentication.parseSAMLResponsePayload(idpResponse.response);
          isLoggedInTheSSO = !!idpSamlResponseData.samlResponseKey;
          if (!isLoggedInTheSSO) {
            _context.next = 24;
            break;
          }
          _context.next = 17;
          return SSOAuthentication.callACSWithSAMLResponse(idpSamlResponseData);
        case 17:
          acsURLResponse = _context.sent;
          if (!acsURLResponse.success) {
            _context.next = 24;
            break;
          }
          _context.next = 21;
          return SSOAuthentication.getUserAccount();
        case 21:
          getUserAccountResponse = _context.sent;
          // if the user is already authenticated, we can't know their username.
          // rather than leaving undefined, we specifically pass null
          // this won't stop the user account being loaded
          SSOAuthentication.loadUserAccount(getUserAccountResponse.response, null);
          isPrecheckSuccess = true;
        case 24:
          if (isPrecheckSuccess) {
            _context.next = 27;
            break;
          }
          _context.next = 27;
          return loginAsPublicCitizen(dispatch);
        case 27:
        case "end":
          return _context.stop();
      }
    }, _callee);
  }));
  return _doSPInitSSOPrecheck.apply(this, arguments);
}
function doIdpInitSSOPrecheck(_x2) {
  return _doIdpInitSSOPrecheck.apply(this, arguments);
}
/**
 * Executes the precheck for Single-Sign-On. The SSO precheck establishes the current logged-in status for the user against the IdP.
 *
 * The precheck is required as a user may already be authenticated with the IdP prior to navigating to Universal Access. When they arrive, we need to determine their authentication status and make initial communication with the application server/IdP (for call interception). This precheck differs between the IdP-init and SP-init flows and also with responses from different application servers.
 * If the user is already authenticated with the IdP, then the user can also be logged-in to Universal Access (by retrieving their user account and loading their user profile).
 * If the user is not already authenticated with the IdP, then the user can continue to use Universal Access as normal as a public/generated user. The user can decide at a later point to login which will use the login function configured for their registered Authentication Method.
 *
 * In situations where Single-Sign-On is not configured, the user is found to be unauthenticated with the IdP, or any error occurs during the precheck, the user will be logged-in to to Universal Access application as a public citizen
 *
 * @param {function} dispatch function that dispatches values to Redux
 */
function _doIdpInitSSOPrecheck() {
  _doIdpInitSSOPrecheck = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2(dispatch) {
    var isPrecheckSuccess, idpLoginInitialResponse, idpSamlResponseData, samlResponseKey, samlRelayState, acsURLResponse, getUserAccountResponse;
    return _regeneratorRuntime.wrap(function _callee2$(_context2) {
      while (1) switch (_context2.prev = _context2.next) {
        case 0:
          // flag to indicate if we need to use fallback behaviour
          isPrecheckSuccess = false; // Call the IdP logininitial URL to get the auth status of the current user against the IdP.
          _context2.next = 3;
          return SSOAuthentication.callIdPLoginInitial();
        case 3:
          idpLoginInitialResponse = _context2.sent;
          // The IdP will respond with either a SAML message or a login form depending on the auth status of the user.
          // If the SAML message contains a SAMLResponseKey, then the user is already authenticated with the IdP.
          idpSamlResponseData = SSOAuthentication.parseSAMLResponsePayload(idpLoginInitialResponse.response);
          if (!idpSamlResponseData.samlResponseKey) {
            _context2.next = 16;
            break;
          }
          samlResponseKey = idpSamlResponseData.samlResponseKey, samlRelayState = idpSamlResponseData.samlRelayState; // The SAMLResponseKey signature is forwarded to the Assertion Consumer Service (ACS) which will validate the SAMLResponseKey.
          _context2.next = 9;
          return SSOAuthentication.callACSWithSAMLResponse({
            samlResponseKey: samlResponseKey,
            samlRelayState: samlRelayState
          });
        case 9:
          acsURLResponse = _context2.sent;
          if (!acsURLResponse.success) {
            _context2.next = 16;
            break;
          }
          _context2.next = 13;
          return SSOAuthentication.getUserAccount();
        case 13:
          getUserAccountResponse = _context2.sent;
          // if the user is already authenticated, we can't know their username.
          // rather than leaving undefined, we specifically pass null
          // this won't stop the user account being loaded
          SSOAuthentication.loadUserAccount(getUserAccountResponse.response, null);
          isPrecheckSuccess = true;
        case 16:
          if (isPrecheckSuccess) {
            _context2.next = 19;
            break;
          }
          _context2.next = 19;
          return loginAsPublicCitizen(dispatch);
        case 19:
        case "end":
          return _context2.stop();
      }
    }, _callee2);
  }));
  return _doIdpInitSSOPrecheck.apply(this, arguments);
}
function executeSSOPreCheck(_x3) {
  return _executeSSOPreCheck.apply(this, arguments);
}
/**
 * This component will be responsible for verifying if the user was logged in an
 * SSO environment and try to use that in order to have it already logged in
 * the application
 *
 * @param {Object} children children that the component will render
 * @param {Object} placeholder place holder that the object will render when it is loading
 */
function _executeSSOPreCheck() {
  _executeSSOPreCheck = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee3(dispatch) {
    return _regeneratorRuntime.wrap(function _callee3$(_context3) {
      while (1) switch (_context3.prev = _context3.next) {
        case 0:
          // set flag to indicate we are verifying against the SSO
          SSOVerifierActions.setSSOVerifier(dispatch, true);

          // SSO prechecks only apply when SSO is enabled. If not, just authenticate as publiccitizen as normal
          if (!Authentication.isSSOEnabled()) {
            _context3.next = 5;
            break;
          }
          if (Authentication.isSSOTypeSP()) {
            // SSO SP-init
            doSPInitSSOPrecheck(dispatch);
          } else {
            // SSO IdP-init
            doIdpInitSSOPrecheck(dispatch);
          }
          _context3.next = 7;
          break;
        case 5:
          _context3.next = 7;
          return loginAsPublicCitizen(dispatch);
        case 7:
          // regardless of precheck, we set a flag in Redux not to re-execute on render again
          SSOVerifierActions.setSSOVerifier(dispatch, false);
        case 8:
        case "end":
          return _context3.stop();
      }
    }, _callee3);
  }));
  return _executeSSOPreCheck.apply(this, arguments);
}
var SSOVerifier = function SSOVerifier(_ref) {
  var children = _ref.children,
    _ref$placeholder = _ref.placeholder,
    placeholder = _ref$placeholder === void 0 ? /*#__PURE__*/React.createElement(AppSpinner, {
      block: true,
      small: true
    }) : _ref$placeholder;
  var dispatch = useDispatch();
  var isVerifyingSSO = useSelector(function (state) {
    return SSOVerifierSelectors.ssoVerifier(state);
  });
  useEffect(function () {
    executeSSOPreCheck(dispatch);
  }, [dispatch]);
  useEffect(function () {
    if (isVerifyingSSO) {
      executeSSOPreCheck(dispatch);
    }
  }, [dispatch, isVerifyingSSO]);
  return isVerifyingSSO ? placeholder : children;
};
export default SSOVerifier;